/**
 * Who has a voucher: upload the list (.xlsx or .csv, add-and-update or
 * replace), add testers by employee number, and look people up — with a
 * remove for anyone who has not ordered yet, and for testers (their test
 * order goes with the voucher; a real order never does).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { FlaskConical, Loader2, RefreshCw, Search, Trash2, Upload, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useConfirm } from '@/hooks/useConfirm'
import { cn } from '@/lib/utils'
import {
  errorDetail,
  formatRand,
  formatSastShort,
  randsToCents,
  splitEmployeeIds,
} from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderImportMode,
  type StaffOrderImportResult,
  type StaffOrderPeopleFilter,
  type StaffOrderPerson,
  type StaffOrderRoundDetail,
} from '@/services/api/staffOrderService'

import { formatCount } from './helpers'
import { ImportResultView } from './ImportResultView'

const FILTERS: Array<{ id: StaffOrderPeopleFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'ordered', label: 'Ordered' },
  { id: 'not_ordered', label: 'Not ordered' },
  { id: 'testers', label: 'Testers' },
]

/** Rows drawn at once; a search narrows the rest down. */
const SHOW_AT_MOST = 200

const ACCEPT = '.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv'

interface VoucherHoldersProps {
  detail: StaffOrderRoundDetail
  /** The round's numbers changed: reload them. */
  onChanged: () => void
}

export function VoucherHolders({ detail, onChanged }: VoucherHoldersProps) {
  const { confirm } = useConfirm()
  const roundId = detail.id
  const ids = {
    file: useId(),
    testerIds: useId(),
    testerAmount: useId(),
    testerCategory: useId(),
    search: useId(),
  }

  // ---- upload
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [mode, setMode] = useState<StaffOrderImportMode>('merge')
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<StaffOrderImportResult | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // ---- testers
  const [testerIds, setTesterIds] = useState('')
  const [testerAmount, setTesterAmount] = useState('')
  const [testerCategory, setTesterCategory] = useState('')
  const [addingTesters, setAddingTesters] = useState(false)
  const [testersResult, setTestersResult] = useState<StaffOrderImportResult | null>(null)
  const [testersError, setTestersError] = useState<string | null>(null)

  // ---- people
  const [filter, setFilter] = useState<StaffOrderPeopleFilter>('all')
  const [people, setPeople] = useState<StaffOrderPerson[]>([])
  const [loadingPeople, setLoadingPeople] = useState(true)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [removingId, setRemovingId] = useState<string | null>(null)
  const request = useRef(0)

  const loadPeople = useCallback(async () => {
    const mine = ++request.current
    setLoadingPeople(true)
    setPeopleError(null)
    try {
      const list = await staffOrderService.listPeople(roundId, filter)
      if (mine === request.current) setPeople(list)
    } catch (err) {
      if (mine === request.current) {
        setPeopleError(errorDetail(err, 'The list could not be loaded. Please try again.'))
      }
    } finally {
      if (mine === request.current) setLoadingPeople(false)
    }
  }, [roundId, filter])

  useEffect(() => {
    void loadPeople()
  }, [loadPeople])

  // A new round on screen: forget the last round's results.
  useEffect(() => {
    setUploadResult(null)
    setUploadError(null)
    setTestersResult(null)
    setTestersError(null)
    setSearch('')
  }, [roundId])

  const campusLabels = useMemo(
    () => new Map(detail.by_campus.map((c) => [c.code, c.label])),
    [detail.by_campus],
  )

  const upload = async () => {
    if (!file || uploading) return
    if (mode === 'replace') {
      const ok = await confirm(
        'Anyone with a voucher who is not in this file will lose it, except people who have already ordered.',
        'Replace the whole list?',
        { confirmText: 'Replace the list', type: 'warning' },
      )
      if (!ok) return
    }
    setUploading(true)
    setUploadError(null)
    setUploadResult(null)
    try {
      const result = await staffOrderService.importPeople(roundId, file, mode)
      setUploadResult(result)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      void loadPeople()
      onChanged()
    } catch (err) {
      setUploadError(errorDetail(err, 'The file could not be loaded. Please check it and try again.'))
    } finally {
      setUploading(false)
    }
  }

  const addTesters = async () => {
    if (addingTesters) return
    setTestersError(null)
    const employeeIds = splitEmployeeIds(testerIds)
    if (employeeIds.length === 0) {
      setTestersError('Enter at least one employee number.')
      return
    }
    const cents = randsToCents(testerAmount)
    if (cents === null || cents <= 0) {
      setTestersError('Enter the voucher amount in Rands, e.g. 1200.')
      return
    }
    setAddingTesters(true)
    setTestersResult(null)
    try {
      const result = await staffOrderService.addTesters(roundId, employeeIds, cents, testerCategory || null)
      setTestersResult(result)
      setTesterIds('')
      void loadPeople()
      onChanged()
    } catch (err) {
      setTestersError(errorDetail(err, 'The testers could not be added. Please try again.'))
    } finally {
      setAddingTesters(false)
    }
  }

  const remove = async (person: StaffOrderPerson) => {
    const who = person.display_name ? `${person.display_name} (${person.employee_id})` : person.employee_id
    const ok = await confirm(
      person.ordered
        ? `${who} is a tester: their voucher and their test order will both go.`
        : `${who} will no longer have a voucher and will not be able to order.`,
      'Remove this voucher?',
      { confirmText: 'Remove', type: 'danger' },
    )
    if (!ok) return
    setRemovingId(person.employee_id)
    try {
      await staffOrderService.removePerson(roundId, person.employee_id)
      setPeople((prev) => prev.filter((p) => p.employee_id !== person.employee_id))
      toast.success('Voucher removed.')
      onChanged()
    } catch (err) {
      toast.error(errorDetail(err, 'The voucher could not be removed. Please try again.'))
    } finally {
      setRemovingId(null)
    }
  }

  const query = search.trim().toLowerCase()
  const matches = query
    ? people.filter(
        (p) =>
          p.employee_id.toLowerCase().includes(query) ||
          (p.display_name ?? '').toLowerCase().includes(query),
      )
    : people
  const shown = matches.slice(0, SHOW_AT_MOST)

  return (
    <Card>
      <CardContent className="space-y-6 p-4 sm:p-5">
        <h4 className="text-sm font-medium text-gray-700">Vouchers</h4>

        {/* Upload */}
        <div className="space-y-3 rounded-lg border border-gray-200 p-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Upload the voucher list</p>
            <p className="text-xs text-gray-500">
              An Excel (.xlsx) or .csv file with a header row: Employee number, Amount (in Rands,
              e.g. 1200), and if you need them, Level and Test (Y/N).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor={ids.file} className="sr-only">
              Voucher list file
            </Label>
            <input
              id={ids.file}
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null)
                setUploadError(null)
              }}
              className="max-w-full text-sm file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:text-heritage-blue-700 hover:file:bg-gray-50"
            />
          </div>
          <fieldset className="space-y-1.5">
            <legend className="sr-only">How to use the file</legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${ids.file}-mode`}
                className="mt-1"
                checked={mode === 'merge'}
                onChange={() => setMode('merge')}
              />
              <span>
                Add and update
                <span className="block text-xs text-gray-500">Everyone already on the list keeps their voucher.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${ids.file}-mode`}
                className="mt-1"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              <span>
                Replace the whole list
                <span className="block text-xs text-gray-500">
                  Anyone not in the file loses their voucher, except people who have already ordered.
                </span>
              </span>
            </label>
          </fieldset>
          <Button type="button" size="sm" onClick={upload} disabled={!file || uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload
          </Button>
          {uploadError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {uploadError}
            </div>
          )}
          {uploadResult && <ImportResultView result={uploadResult} heading="The list is loaded." />}
        </div>

        {/* Testers */}
        <div className="space-y-3 rounded-lg border border-gray-200 p-3">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
              <FlaskConical className="h-4 w-4 text-gray-500" />
              Add testers
            </p>
            <p className="text-xs text-gray-500">
              Testers see the order page before ordering opens, so the page and the emails can be
              checked. They are left out of the numbers and the download. Give them a category to
              see the category and the suggested apparel in their email.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_10rem]">
            <div className="space-y-1.5">
              <Label htmlFor={ids.testerIds}>Employee numbers</Label>
              <Input
                id={ids.testerIds}
                value={testerIds}
                onChange={(e) => setTesterIds(e.target.value)}
                placeholder="Separated by commas or spaces"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={ids.testerAmount}>Voucher (Rands)</Label>
              <Input
                id={ids.testerAmount}
                value={testerAmount}
                onChange={(e) => setTesterAmount(e.target.value)}
                placeholder="e.g. 1200"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={ids.testerCategory}>Category</Label>
              <select
                id={ids.testerCategory}
                className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800"
                value={testerCategory}
                onChange={(e) => setTesterCategory(e.target.value)}
              >
                <option value="">None</option>
                <option value="Category A">Category A</option>
                <option value="Category B">Category B</option>
                <option value="Category C">Category C</option>
              </select>
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addTesters} disabled={addingTesters}>
            {addingTesters ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Add testers
          </Button>
          {testersError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {testersError}
            </div>
          )}
          {testersResult && <ImportResultView result={testersResult} heading="Testers added." />}
        </div>

        {/* People */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border" role="tablist" aria-label="Show">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  onClick={() => setFilter(f.id)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-colors',
                    filter === f.id ? 'bg-heritage-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative min-w-[12rem] flex-1">
              <Label htmlFor={ids.search} className="sr-only">
                Search by name or employee number
              </Label>
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                id={ids.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or employee number"
                className="pl-8"
              />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => void loadPeople()} disabled={loadingPeople}>
              <RefreshCw className={cn('h-4 w-4', loadingPeople && 'animate-spin')} />
              Refresh
            </Button>
          </div>

          {peopleError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {peopleError}
            </div>
          ) : loadingPeople && people.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : matches.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              {query ? 'Nobody matches that search.' : 'Nobody here yet.'}
            </p>
          ) : (
            <>
              <div className="rounded-md border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Employee number</TableHead>
                      <TableHead>Voucher</TableHead>
                      <TableHead>Invited</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="w-10">
                        <span className="sr-only">Remove</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shown.map((p) => (
                      <TableRow key={p.employee_id}>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-gray-900">{p.display_name || '—'}</span>
                            {p.is_test && (
                              <Badge className="border-transparent bg-amber-100 px-1.5 py-0 text-[10px] text-amber-800 hover:bg-amber-100">
                                Test
                              </Badge>
                            )}
                          </div>
                          {p.job_title && <div className="text-xs text-gray-500">{p.job_title}</div>}
                        </TableCell>
                        <TableCell className="tabular-nums">{p.employee_id}</TableCell>
                        <TableCell>
                          <div className="tabular-nums">{formatRand(p.amount_cents)}</div>
                          {p.tier_label && <div className="text-xs text-gray-500">{p.tier_label}</div>}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {p.invited_at ? formatSastShort(p.invited_at) : 'Not yet'}
                          {p.reminded_at && (
                            <div className="text-gray-500">Reminded {formatSastShort(p.reminded_at)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.ordered ? (
                            <>
                              <div className="tabular-nums">{formatRand(p.total_cents)}</div>
                              <div className="text-xs text-gray-500">
                                {p.delivery_campus ? campusLabels.get(p.delivery_campus) ?? p.delivery_campus : ''}
                                {p.updated_at ? ` · ${formatSastShort(p.updated_at)}` : ''}
                              </div>
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">Not ordered</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* A real order is never removed with its voucher; a tester's test order is. */}
                          {(!p.ordered || p.is_test) && (
                            <button
                              type="button"
                              onClick={() => remove(p)}
                              disabled={removingId === p.employee_id}
                              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                              aria-label={`Remove the voucher for ${p.display_name || p.employee_id}`}
                              title="Remove this voucher"
                            >
                              {removingId === p.employee_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-gray-500">
                {matches.length > SHOW_AT_MOST
                  ? `Showing ${formatCount(SHOW_AT_MOST)} of ${formatCount(matches.length)}. Search to find someone else.`
                  : `${formatCount(matches.length)} ${matches.length === 1 ? 'person' : 'people'}.`}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
